class ViewportTracker {
  constructor(options) {
    if (!options || !options.trackedElement || !options.stickyElement) {
      throw new Error('trackedElement and stickyElement must be provided');
    }

    this.trackedElement = options.trackedElement;
    this.stickyElement = options.stickyElement;
    this.secondaryStickyElement = options.secondaryStickyElement || null;
    this.stickyState = options.initialStickyState || 'visible';
    this.throttleDelay = options.throttleDelay || 100;
    this.offset = options.offset || 0;
    this.fadeInDuration = options.fadeInDuration || 300;
    this.fadeOutDuration = options.fadeOutDuration || 300;
    this.maxWidth = options.maxWidth || Infinity;

    // Secondary element spacing from the primary element
    this.secondarySpacing = options.secondarySpacing || 0;

    this.lastPosition = null;
    this.ticking = false;
    this.isActive = false;

    this.init();
  }

  init() {
    this.updateState = this.updateState.bind(this);
    this.checkViewportWidth = this.checkViewportWidth.bind(this);
    window.addEventListener('scroll', this.throttle(this.updateState, this.throttleDelay));
    window.addEventListener(
      'resize',
      this.throttle(() => {
        this.checkViewportWidth();
        this.updateState();
      }, this.throttleDelay)
    );
    this.setupStickyElementStyles();
    this.checkViewportWidth();
    this.updateState();
  }

  setupStickyElementStyles() {
    this.stickyElement.style.transition = `opacity ${this.fadeInDuration}ms, position 0s`;
    this.stickyElement.style.opacity = '0';
    this.stickyElement.style.zIndex = '2';

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.style.transition = `opacity ${this.fadeInDuration}ms, position 0s`;
      this.secondaryStickyElement.style.opacity = '0';
      this.secondaryStickyElement.style.zIndex = '1';
    }
  }

  throttle(func, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) return;
      lastCall = now;
      return func(...args);
    };
  }

  checkViewportWidth() {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    this.isActive = viewportWidth <= this.maxWidth;
    if (!this.isActive) {
      this.resetStickyElement();
    }
  }

  resetStickyElement() {
    this.stickyElement.style.position = 'static';
    this.stickyElement.style.top = 'auto';
    this.stickyElement.style.opacity = '1';

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.style.position = 'static';
      this.secondaryStickyElement.style.top = 'auto';
      this.secondaryStickyElement.style.opacity = '1';
    }
  }

  updateSecondaryPosition() {
    if (!this.secondaryStickyElement || !this.isActive) return;

    const primaryRect = this.stickyElement.getBoundingClientRect();
    const primaryHeight = primaryRect.height;

    if (this.stickyElement.style.position === 'sticky') {
      this.secondaryStickyElement.style.position = 'sticky';
      this.secondaryStickyElement.style.top = `${primaryHeight + this.secondarySpacing}px`;
      this.secondaryStickyElement.style.width = '100%';
    } else {
      this.secondaryStickyElement.style.position = 'static';
      this.secondaryStickyElement.style.top = 'auto';
    }
  }

  getElementPosition() {
    const rect = this.trackedElement.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    if (rect.top - this.offset > windowHeight) return 'above';
    if (rect.bottom + this.offset < 0) return 'below';
    return 'visible';
  }

  updateState() {
    if (this.ticking || !this.isActive) return;

    this.ticking = true;
    requestAnimationFrame(() => {
      const position = this.getElementPosition();
      if (position !== this.lastPosition) {
        this.lastPosition = position;
        this.toggleStickyPosition(position);
      }
      this.ticking = false;
    });
  }

  toggleStickyPosition(position) {
    const isSticky = position === this.stickyState;
    if (isSticky) {
      this.fadeIn();
    } else {
      this.fadeOut();
    }
  }

  fadeIn() {
    this.stickyElement.style.position = 'sticky';
    this.stickyElement.style.top = '0';
    this.stickyElement.style.opacity = '1';

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.style.opacity = '1';
      this.updateSecondaryPosition();
    }
  }

  fadeOut() {
    this.stickyElement.style.opacity = '0';
    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.style.opacity = '0';
    }

    setTimeout(() => {
      if (this.stickyElement.style.opacity === '0') {
        this.stickyElement.style.position = 'static';
        this.stickyElement.style.top = 'auto';

        if (this.secondaryStickyElement) {
          this.secondaryStickyElement.style.position = 'static';
          this.secondaryStickyElement.style.top = 'auto';
        }
      }
    }, this.fadeOutDuration);
  }

  setStickyState(state) {
    if (['visible', 'above', 'below'].includes(state)) {
      this.stickyState = state;
      this.updateState();
    } else {
      console.error('Invalid state. Use "visible", "above", or "below".');
    }
  }

  setOffset(offset) {
    this.offset = offset;
    this.updateState();
  }

  setSecondarySpacing(spacing) {
    this.secondarySpacing = spacing;
    this.updateState();
  }

  setFadeDuration(fadeInDuration, fadeOutDuration) {
    this.fadeInDuration = fadeInDuration;
    this.fadeOutDuration = fadeOutDuration;
    this.setupStickyElementStyles();
  }

  setMaxWidth(maxWidth) {
    this.maxWidth = maxWidth;
    this.checkViewportWidth();
    this.updateState();
  }
}

// Usage example:
const stickyTracker = new ViewportTracker({
  trackedElement: document.querySelector('.product-form__submit'),
  stickyElement: document.querySelector('.product__media-wrapper'),
  secondaryStickyElement: document.querySelector('.product__price-wrapper'),
  initialStickyState: 'above',
  offset: -200,
  secondarySpacing: 0, // Space between primary and secondary elements
  fadeInDuration: 200,
  fadeOutDuration: 200,
  maxWidth: 749,
});
